import { create } from "zustand";
import { MAX_TERMINALS_PER_USER, newId, type WorktreeScript } from "@rivets/core";

export type TerminalSessionStatus = "idle" | "creating" | "attaching" | "open" | "recovering" | "detached" | "exited" | "offline" | "failed";
/** `"v2"` once this tab has asked the worker for the terminal (create or
 * attach); undefined while it is still only a tab. */
export type TerminalMode = "v2" | undefined;

export interface ResizeLease {
  leaseId: string;
  epoch: number;
  expiresAt: number;
}

/** What a session is showing, when it is not a shell the user opened. A script
 * session is worker-owned: the client never creates or closes it, and never
 * sends it input. */
export interface BrowserScriptSession {
  id: string;
  kind: "setup" | "run" | "archive";
  name: string;
  icon: string;
  state: WorktreeScript["state"];
  exitCode?: number;
}

export interface BrowserTerminalSession {
  /** Stable client-selected terminal id. This is never the React tab id. */
  terminalId: string;
  worktreeId: string;
  number: number;
  placement?: "workspace";
  program?: "claude" | "codex";
  /** Present only for a repository script's output terminal. */
  script?: BrowserScriptSession;
  status: TerminalSessionStatus;
  mode: TerminalMode;
  /** Persisted after terminal.created so a fresh browser page attaches by ID. */
  createdV2: boolean;
  incarnation: number;
  lastSequence: number;
  nextInputSequence: number;
  /** Stable across reloads and direct/relay fallback, so another viewer's
   * sequence numbers can never suppress this producer's input. */
  producerId: string;
  lease?: ResizeLease;
  message?: string;
}

export function mergeTerminalSessionPatch(session: BrowserTerminalSession, patch: Partial<BrowserTerminalSession>): BrowserTerminalSession {
  // Creation is a fact about the worker, so no later patch can take it back.
  const createdV2 = session.createdV2 || patch.createdV2 === true;
  return { ...session, ...patch, createdV2 };
}

export function replaceUnavailableTerminal(
  session: BrowserTerminalSession,
  terminalId = newId("term"),
  producerId = newId("prod"),
): BrowserTerminalSession {
  return {
    ...session,
    terminalId,
    status: "failed",
    mode: undefined,
    createdV2: false,
    incarnation: 0,
    lastSequence: -1,
    nextInputSequence: 0,
    producerId,
    lease: undefined,
    message: "This saved terminal is no longer available. Retry to start a new shell.",
  };
}

type StoredSession = Pick<BrowserTerminalSession, "terminalId" | "worktreeId" | "number" | "createdV2" | "producerId" | "nextInputSequence" | "placement" | "program">;
const STORAGE_KEY = "rivets.terminal-v2.sessions";

function readStored(): Record<string, BrowserTerminalSession[]> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const values = raw ? JSON.parse(raw) as StoredSession[] : [];
    const byWorktree: Record<string, BrowserTerminalSession[]> = {};
    for (const value of values) {
      if (!value || typeof value.terminalId !== "string" || typeof value.worktreeId !== "string" || !Number.isInteger(value.number)) continue;
      const producerId = typeof value.producerId === "string" && /^prod_[0-9abcdefghjkmnpqrstvwxyz]{16}$/.test(value.producerId) ? value.producerId : newId("prod");
      const nextInputSequence = Number.isSafeInteger(value.nextInputSequence) && value.nextInputSequence >= 0 ? value.nextInputSequence : 0;
      (byWorktree[value.worktreeId] ??= []).push({ ...value, producerId, nextInputSequence, createdV2: value.createdV2 === true, status: "idle", mode: undefined, incarnation: 0, lastSequence: -1 });
    }
    return byWorktree;
  } catch {
    return {};
  }
}

function persist(sessions: Record<string, BrowserTerminalSession[]>): void {
  try {
    // Script sessions are worker-owned and re-announced on every connect;
    // persisting them would resurrect a tab for a script the repo has dropped.
    const values = Object.values(sessions).flat()
      .filter((session) => !session.script)
      .map(({ terminalId, worktreeId, number, createdV2, producerId, nextInputSequence, placement, program }) => ({ terminalId, worktreeId, number, createdV2, producerId, nextInputSequence, placement, program }));
    localStorage.setItem(STORAGE_KEY, JSON.stringify(values));
  } catch {
    // Private browsing/storage exhaustion only loses the convenience cache; the
    // current in-memory stable id remains valid for this page lifetime.
  }
}

interface TerminalStore {
  sessionsByWorktree: Record<string, BrowserTerminalSession[]>;
  add: (worktreeId: string, options?: Pick<BrowserTerminalSession, "placement" | "program">) => BrowserTerminalSession | undefined;
  patch: (terminalId: string, patch: Partial<BrowserTerminalSession>) => void;
  replaceUnavailable: (terminalId: string) => BrowserTerminalSession | undefined;
  remove: (terminalId: string) => void;
  /** Reconcile this worktree's script tabs against the worker's latest set. */
  syncScripts: (worktreeId: string, scripts: WorktreeScript[]) => void;
  /** Script terminals this viewer closed, and the run they were closed on. */
  dismissedScripts: Record<string, number>;
  /** Put a script's transcript away without touching the worker's terminal or
   * the run. It comes back on the script's next run, never before. */
  dismissScript: (terminalId: string, incarnation: number) => void;
  /** Undo a dismissal, for the one case that always means "show me this": the
   * user just pressed Run on it. */
  restoreScript: (terminalId: string) => void;
  reset: () => void;
}

export const useTerminalSessionStore = create<TerminalStore>((set, get) => ({
  sessionsByWorktree: typeof window === "undefined" ? {} : readStored(),
  // Never persisted: a dismissal is about the tab in front of you, not the
  // repository, so a reload shows the worktree's scripts as the worker has them.
  dismissedScripts: {},
  reset() { persist({}); set({ sessionsByWorktree: {}, dismissedScripts: {} }); },
  add(worktreeId, options = {}) {
    const current = get().sessionsByWorktree[worktreeId] ?? [];
    // Script tabs are worker-owned workspace output, not shells this user
    // opened, so they never consume the per-user budget — the API makes the
    // same exclusion. Counting them would let a repository with a dozen run
    // scripts lock a user out of opening a shell at all.
    if (current.filter((session) => !session.script).length >= MAX_TERMINALS_PER_USER) return undefined;
    const session: BrowserTerminalSession = {
      ...options, terminalId: newId("term"), worktreeId, number: (Math.max(0, ...current.map((item) => item.number)) + 1),
      status: "idle", mode: undefined, createdV2: false, incarnation: 0, lastSequence: -1, nextInputSequence: 0, producerId: newId("prod"),
    };
    const sessionsByWorktree = { ...get().sessionsByWorktree, [worktreeId]: [...current, session] };
    persist(sessionsByWorktree); set({ sessionsByWorktree });
    return session;
  },
  patch(terminalId, patch) {
    const sessionsByWorktree = Object.fromEntries(Object.entries(get().sessionsByWorktree).map(([worktreeId, sessions]) => [
      worktreeId, sessions.map((session) => session.terminalId === terminalId
        ? mergeTerminalSessionPatch(session, patch)
        : session),
    ]));
    if ("createdV2" in patch || "producerId" in patch || "nextInputSequence" in patch) persist(sessionsByWorktree);
    set({ sessionsByWorktree });
  },
  replaceUnavailable(terminalId) {
    let replacement: BrowserTerminalSession | undefined;
    const sessionsByWorktree = Object.fromEntries(Object.entries(get().sessionsByWorktree).map(([worktreeId, sessions]) => [
      worktreeId, sessions.map((session) => {
        if (session.terminalId !== terminalId) return session;
        replacement = replaceUnavailableTerminal(session);
        return replacement;
      }),
    ]));
    if (replacement) { persist(sessionsByWorktree); set({ sessionsByWorktree }); }
    return replacement;
  },
  syncScripts(worktreeId, scripts) {
    const current = get().sessionsByWorktree[worktreeId] ?? [];
    const withTerminals = scripts.filter((script) => script.terminalId !== undefined);
    const wanted = new Map(withTerminals.map((script) => [script.terminalId!, script]));
    let changed = false;
    const next: BrowserTerminalSession[] = [];
    for (const session of current) {
      if (!session.script) { next.push(session); continue; }
      const script = wanted.get(session.terminalId);
      // A script the repository removed loses its tab; its terminal is the
      // worker's to reap.
      if (!script) { changed = true; continue; }
      wanted.delete(session.terminalId);
      const descriptor = describeScript(script);
      // A re-run is a new incarnation of the same terminal, and normally the
      // worker's `terminal.created` resets this session's cursor. `script.state`
      // carries the incarnation too, so a lost lifecycle frame cannot strand a
      // viewer on the previous run's cursor watching a terminal that never
      // draws again.
      const restarted = script.terminalIncarnation !== undefined
        && script.terminalIncarnation > session.incarnation;
      if (!restarted && sameScript(session.script, descriptor)) { next.push(session); continue; }
      changed = true;
      next.push(restarted
        ? { ...session, script: descriptor, status: "idle", mode: undefined, incarnation: 0, lastSequence: -1 }
        : { ...session, script: descriptor });
    }
    const dismissed = get().dismissedScripts;
    const restored: string[] = [];
    for (const script of wanted.values()) {
      const closedOn = dismissed[script.terminalId!];
      // Closed by hand and not run since: the transcript stays away until the
      // script produces a new one.
      if (closedOn !== undefined) {
        if ((script.terminalIncarnation ?? 0) <= closedOn) continue;
        restored.push(script.terminalId!);
      }
      changed = true;
      next.push({
        terminalId: script.terminalId!,
        worktreeId,
        // Script tabs are labelled by name, so the shell numbering never sees them.
        number: 0,
        script: describeScript(script),
        status: "idle",
        mode: undefined,
        // The worker already created this terminal; the client attaches to it
        // rather than asking for a new one.
        createdV2: true,
        incarnation: 0,
        lastSequence: -1,
        nextInputSequence: 0,
        producerId: newId("prod"),
      });
    }
    if (!changed) return;
    const remaining = restored.length === 0
      ? dismissed
      : Object.fromEntries(Object.entries(dismissed).filter(([id]) => !restored.includes(id)));
    // Deliberately not persisted: the worker owns these and republishes them.
    set({ sessionsByWorktree: { ...get().sessionsByWorktree, [worktreeId]: next }, dismissedScripts: remaining });
  },
  dismissScript(terminalId, incarnation) {
    set({ dismissedScripts: { ...get().dismissedScripts, [terminalId]: incarnation } });
  },
  restoreScript(terminalId) {
    if (!(terminalId in get().dismissedScripts)) return;
    const { [terminalId]: _closed, ...rest } = get().dismissedScripts;
    set({ dismissedScripts: rest });
  },
  remove(terminalId) {
    const sessionsByWorktree = Object.fromEntries(Object.entries(get().sessionsByWorktree)
      .map(([worktreeId, sessions]): [string, BrowserTerminalSession[]] => [worktreeId, sessions.filter((session) => session.terminalId !== terminalId)])
      .filter(([, sessions]) => sessions.length > 0));
    persist(sessionsByWorktree); set({ sessionsByWorktree });
  },
}));

export type TerminalOrder = "accept" | "stale" | "recover";

/** Incarnation changes are never applied to an old emulator; attach establishes
 * the authoritative incarnation before the client accepts output. */
export function terminalOrder(session: Pick<BrowserTerminalSession, "incarnation" | "lastSequence">, incarnation: number, sequence: number): TerminalOrder {
  if (incarnation < session.incarnation || (incarnation === session.incarnation && sequence <= session.lastSequence)) return "stale";
  if (incarnation > session.incarnation || sequence !== session.lastSequence + 1) return "recover";
  return "accept";
}

function describeScript(script: WorktreeScript): BrowserScriptSession {
  return {
    id: script.id,
    kind: script.kind,
    name: script.name,
    icon: script.icon,
    state: script.state,
    ...(script.exitCode === undefined ? {} : { exitCode: script.exitCode }),
  };
}

function sameScript(left: BrowserScriptSession, right: BrowserScriptSession): boolean {
  return left.id === right.id && left.kind === right.kind && left.name === right.name &&
    left.icon === right.icon && left.state === right.state && left.exitCode === right.exitCode;
}
